export type Chapter = {
  name: string;
  id: string | null;
  analyticsId: string;
};

export const DEFAULT_UNLABELLED_CHAPTER_ANALYTICS_ID =
  "btn_chapter_progress_chapter_default_unlabelled_id";

/**
 * Distance (px) from the top of the viewport to the active-detection line. A
 * chapter is considered "past" the line once its top sits at or above this
 * offset. Keep this in sync with DETECTION_LINE_ROOT_MARGIN below.
 */
export const DETECTION_OFFSET_PX = 150;

/**
 * Read chapters (elements marked with `data-chapter-name`) and the optional
 * `[data-chapter-end]` fade-out marker from the DOM, in document order. Returns
 * empty results during SSR or when no chapters are present.
 *
 * `ids` carries each chapter's deep-link anchor (its `id` or `data-chapter-id`)
 * or `null` when it has none. Hash syncing is opt-in: a consumer enables it by
 * giving its chapter elements ids; one that doesn't (e.g. the Storybook
 * fixtures) leaves `ids` all-null and the hook never touches the URL.
 */
export function discoverChapters(): {
  chapterEls: HTMLElement[];
  names: string[];
  ids: (string | null)[];
  analyticsIds: string[];
  endEl: HTMLElement | null;
} {
  if (typeof window === "undefined") {
    return {
      chapterEls: [],
      names: [],
      ids: [],
      analyticsIds: [],
      endEl: null,
    };
  }
  const chapterEls = [
    ...document.querySelectorAll<HTMLElement>("[data-chapter-name]"),
  ];
  return {
    chapterEls,
    names: chapterEls.map((el) => el.dataset.chapterName ?? ""),
    ids: chapterEls.map((el) => el.id || el.dataset.chapterId || null),
    analyticsIds: chapterEls.map(
      (el) =>
        el.dataset.chapterAnalyticsId ||
        DEFAULT_UNLABELLED_CHAPTER_ANALYTICS_ID,
    ),
    endEl: document.querySelector<HTMLElement>("[data-chapter-end]"),
  };
}
